import type {
  RecoveryControls,
  RecoveryObservationError,
  RecoveryTrigger,
} from '@github-ui/ahp-relay/relay-client-recovery'
import {createPresentationLossReporter} from '@github-ui/ahp-relay/result-presentation-health'
import {isHostInstanceId} from '@github-ui/ahp-relay/host-instance-evidence'
import type {BrowserObservationTime} from './browser-observation-clock'
import {
  CLIENT_RECOVERY_POLICY,
  RECOVERY_STAGES,
  type ClientRecoveryEvent,
  type ClientRecoveryFact,
  type RecoveryBinding,
  type RecoveryDisposition,
  type RecoveryInput,
  type RecoveryInventory,
  type RecoveryItem,
  type RecoveryObservation,
  type RecoveryPath,
  type RecoveryReason,
  type RecoverySource,
  type RecoverySourceReference,
  type RecoveryStage,
} from './client-recovery-contract'

export const MAX_RECOVERY_EPISODES = 64
export const MAX_RECOVERY_ITEMS = 128
export const MAX_RECOVERY_SOURCES = 256

interface Dependencies {
  now(): BrowserObservationTime
  nextSequence(): number
  role(): ClientRecoveryFact['role']
  visibility(): ClientRecoveryFact['visibility']
  emit(event: ClientRecoveryEvent): void | Promise<void>
  reportError(reason: RecoveryObservationError): void | Promise<void>
}
interface Proof {
  id: string
  time: BrowserObservationTime
}
interface DeclaredItem {
  item: RecoveryItem
  declaration: string
  disposition?: RecoveryDisposition
}
interface Episode {
  id: string
  eventId: string
  start: BrowserObservationTime
  trigger: RecoveryTrigger
  surface: ClientRecoveryFact['surface']
  binding: RecoveryBinding
  inventory: RecoveryInventory
  items: Map<string, DeclaredItem>
  sources: Map<string, RecoverySourceReference>
  available: Map<RecoveryStage, Proof>
  succeeded: Map<RecoveryStage, Proof>
  controls: Set<string>
  blocked: Set<string>
  generation?: string
  attempt?: string
  path: RecoveryPath
  failure?: number
  incompleteAttempt?: boolean
  visibilityInterrupted?: boolean
  closed?: number
}

const itemKey = (item: RecoveryItem) => JSON.stringify([item.item_kind, item.item_id, item.revision_id ?? null])
const identifier = (value: string) => value.length > 0 && value.length <= 512
const terminal = (disposition: RecoveryDisposition) =>
  ['applied', 'completed', 'transferred', 'user_withdrawn', 'system_failed'].includes(disposition.disposition)

export function createBrowserClientRecoveryTracker(
  dependencies: Dependencies,
  context: {
    source_id: string
    surface: ClientRecoveryFact['surface'] | (() => ClientRecoveryFact['surface'])
    binding: RecoveryBinding
  },
) {
  let episode: Episode | undefined
  let count = 0
  let disposed = false
  let exhausted = false
  const availabilityByInput = new WeakMap<RecoverySourceReference, Map<RecoveryStage, Proof>>()
  const reported = new Set<RecoveryObservationError>()
  const report = async (reason: RecoveryObservationError) => {
    if (reported.has(reason)) return
    reported.add(reason)
    try {
      await dependencies.reportError(reason)
    } catch {
      // eslint-disable-next-line no-console
      console.error('Unable to report client recovery observation error')
    }
  }
  const observe = <T>(callback: () => T): T | undefined => {
    try {
      return callback()
    } catch {
      void report('observer')
      return undefined
    }
  }
  const deliver = async (fact: ClientRecoveryEvent) => {
    try {
      await dependencies.emit(fact)
    } catch {
      void report('emission')
    }
  }
  const stamp = (time: BrowserObservationTime) => {
    const sequence = dependencies.nextSequence()
    if (
      !identifier(time.clockId) ||
      !Number.isSafeInteger(sequence) ||
      sequence < 0 ||
      !Number.isFinite(time.observedAtMs) ||
      time.observedAtMs < 0 ||
      !Number.isFinite(time.monotonicMs) ||
      time.monotonicMs < 0
    ) {
      void report('clock')
      return
    }
    return {
      event_id: `${time.clockId}/${sequence}`,
      clock_id: time.clockId,
      sequence,
      observed_at_ms: time.observedAtMs,
      monotonic_ms: time.monotonicMs,
    }
  }
  const emit = (observation: RecoveryObservation, time: BrowserObservationTime): string | undefined => {
    const current = episode
    if (!current || time.clockId !== current.start.clockId || time.monotonicMs < current.start.monotonicMs) {
      void report('clock')
      return
    }
    const identity = stamp(time)
    if (!identity) return
    current.eventId ||= identity.event_id
    if (observation.observation === 'attempt_started') current.attempt = identity.event_id
    const fact: ClientRecoveryFact = {
      ...CLIENT_RECOVERY_POLICY,
      ...current.binding,
      ...identity,
      source_id: context.source_id,
      episode_id: current.id,
      episode_event_id: current.eventId,
      episode_monotonic_ms: current.start.monotonicMs,
      required_obligations: 'reconnect|state_application|pending_commands|client_tools|controls',
      trigger: current.trigger,
      recovery_path: current.path,
      surface: current.surface,
      role: dependencies.role(),
      visibility: dependencies.visibility(),
      ...(current.generation ? {connection_generation: current.generation} : {}),
      ...(current.attempt ? {attempt_event_id: current.attempt} : {}),
      ...observation,
      ...(observation.observation === 'item_declared' ? {item_declaration_event_id: identity.event_id} : {}),
      ...(observation.observation === 'source_observed' ? {source_event_id: identity.event_id} : {}),
    }
    void deliver(fact)
    return identity.event_id
  }
  let lossClock = ''
  let lossReason: 'observation_capacity' | 'entry_unverified' = 'observation_capacity'
  const loss = createPresentationLossReporter({
    now: () => {
      const time = dependencies.now()
      lossClock = time.clockId
      return {...time, visibility: dependencies.visibility()}
    },
    id: () => '',
    emit: value => {
      const time = {...value, clockId: lossClock}
      const identity = stamp(time)
      if (!identity) return
      void deliver({
        ...CLIENT_RECOVERY_POLICY,
        ...context.binding,
        ...identity,
        observation: 'coverage_lost',
        source_id: context.source_id,
        source_event_id: identity.event_id,
        evidence_scope: 'source',
        reason: lossReason,
        surface: typeof context.surface === 'function' ? context.surface() : context.surface,
        role: 'unknown',
        visibility: value.visibility,
        suppressed_observations: value.suppressed_observations,
      })
    },
  })
  const coverageLost = (
    opportunity: string,
    reason: 'observation_capacity' | 'entry_unverified' = 'observation_capacity',
  ) =>
    observe(() => {
      if (disposed) return
      if (reason === 'observation_capacity') {
        exhausted = true
        void report('observation_capacity')
      }
      lossReason = reason
      loss(`${reason}/${opportunity}`)
    })
  const blocked = (stage: RecoveryStage, reason: RecoveryReason, time = dependencies.now()) => {
    const current = episode
    if (!current || current.closed !== undefined) return
    if (reason === 'visibility_interrupted') current.visibilityInterrupted = true
    const key = `${stage}/${reason}`
    if (current.blocked.has(key)) return
    current.blocked.add(key)
    emit({observation: 'blocked', stage, reason}, time)
  }
  const fail = (stage: RecoveryStage, reason: RecoveryReason, time = dependencies.now()) =>
    observe(() => {
      const current = episode
      if (!current || (current.closed !== undefined && time.monotonicMs > current.closed)) return
      if (time.clockId !== current.start.clockId || time.monotonicMs < current.start.monotonicMs) {
        void report('clock')
        return
      }
      current.failure = Math.min(current.failure ?? Infinity, time.monotonicMs)
      emit({observation: 'failed', stage, reason}, time)
    })
  const sourceValid = (source: RecoverySourceReference) =>
    episode?.sources.get(source.event_id) === source && source.attempt_event_id === episode.attempt
  const available = (stage: RecoveryStage, input_kind: RecoveryInput, source: RecoverySourceReference) => {
    const current = episode
    if (!current || !sourceValid(source)) return
    if (stage !== 'state_application' && current.available.has(stage)) return
    const previous = availabilityByInput.get(source)?.get(stage)
    if (previous) {
      current.available.set(stage, previous)
      return
    }
    const id = emit({observation: 'stage_available', stage, input_kind, source_event_id: source.event_id}, source.time)
    if (id) {
      const proof = {id, time: source.time}
      const inputs = availabilityByInput.get(source) ?? new Map<RecoveryStage, Proof>()
      inputs.set(stage, proof)
      availabilityByInput.set(source, inputs)
      current.available.set(stage, proof)
    }
  }
  const finish = (time: BrowserObservationTime) => {
    const current = episode
    if (!current || exhausted || current.closed !== undefined) return
    const [reconnect, state, commands, tools, controls] = RECOVERY_STAGES.map(stage => current.succeeded.get(stage))
    if (!reconnect || !state || !commands || !tools || !controls) return
    current.closed = time.monotonicMs
    if (current.failure !== undefined || current.incompleteAttempt || current.visibilityInterrupted) return
    emit(
      {
        observation: 'completed',
        reconnect_event_id: reconnect.id,
        state_application_event_id: state.id,
        pending_commands_event_id: commands.id,
        client_tools_event_id: tools.id,
        controls_event_id: controls.id,
        closure_time_ms: time.monotonicMs,
      },
      time,
    )
  }
  const succeed = (
    stage: RecoveryStage,
    source: RecoverySourceReference,
    state?: {applied_session_id: string; applied_chat_id: string; state_consistent: true},
    time = source.time,
  ) => {
    const current = episode
    const start = current?.available.get(stage)
    if (!current || !start || !sourceValid(source) || exhausted || current.closed !== undefined) return
    if (stage !== 'state_application' && current.succeeded.has(stage)) return
    const elapsed = time.monotonicMs - start.time.monotonicMs
    if (elapsed < 0 || time.clockId !== start.time.clockId) return void report('clock')
    const id = emit(
      {
        observation: 'stage_succeeded',
        stage,
        source_event_id: source.event_id,
        stage_available_event_id: start.id,
        stage_elapsed_ms: elapsed,
        ...state,
      },
      time,
    )
    if (id) current.succeeded.set(stage, {id, time})
    finish(time)
  }
  const reconcileStage = (
    stage: 'pending_commands' | 'client_tools',
    source: RecoverySourceReference,
    time = source.time,
  ) => {
    const current = episode
    if (!current?.succeeded.has('state_application')) return
    const command = stage === 'pending_commands'
    if (current.inventory[command ? 'commands_manifest' : 'tools_manifest'] !== 'complete') {
      current.succeeded.delete(stage)
      blocked(stage, 'prestate_unknown', time)
      return
    }
    available(stage, command ? 'pending_manifest' : 'tool_manifest', source)
    const items = [...current.items.values()].filter(
      value => value.item.item_kind === (command ? 'command' : 'client_tool'),
    )
    if (items.some(value => command && !value.item.revision_id)) {
      current.succeeded.delete(stage)
      blocked(stage, 'revision_unknown', time)
      return
    }
    if (items.some(value => !value.disposition || value.disposition.disposition === 'unresolved')) {
      current.succeeded.delete(stage)
      blocked(stage, 'item_unresolved', time)
      return
    }
    succeed(stage, source, undefined, time)
  }
  const source = (input: RecoverySource, time = dependencies.now()): RecoverySourceReference | undefined =>
    observe(() => {
      const current = episode
      if (!current || current.closed !== undefined || disposed) return
      if (exhausted || current.sources.size >= MAX_RECOVERY_SOURCES) {
        coverageLost(input.source_kind)
        return
      }
      const projection: RecoverySource = {
        source_kind: input.source_kind,
        ...(input.source_session_id ? {source_session_id: input.source_session_id} : {}),
        ...(input.source_chat_id ? {source_chat_id: input.source_chat_id} : {}),
        ...(input.state_path ? {state_path: input.state_path} : {}),
        ...(isHostInstanceId(input.before_host_instance_id)
          ? {before_host_instance_id: input.before_host_instance_id}
          : {}),
        ...(isHostInstanceId(input.host_instance_id)
          ? {host_instance_id: input.host_instance_id, authority: input.authority}
          : {}),
        ...(input.client_relation ? {client_relation: input.client_relation} : {}),
      }
      if (
        input.authority === 'host_instance_v1' &&
        isHostInstanceId(input.before_host_instance_id) &&
        isHostInstanceId(input.host_instance_id) &&
        input.before_host_instance_id !== input.host_instance_id
      ) {
        current.path = 'host_restart'
      }
      const id = emit({observation: 'source_observed', source_event_id: '', ...projection}, time)
      if (!id) return
      const reference = {...projection, event_id: id, attempt_event_id: current.attempt, time}
      current.sources.set(id, reference)
      if (input.source_kind === 'credentials') available('reconnect', 'credentials', reference)
      if (input.source_kind === 'snapshot' || input.source_kind === 'replay') {
        available('state_application', 'provided_state', reference)
      }
      return reference
    })
  return {
    get active() {
      return Boolean(episode && episode.closed === undefined && !disposed)
    },
    get exhausted() {
      return exhausted
    },
    get episodeId() {
      return episode?.id
    },
    coverageLost,
    blocked: (stage: RecoveryStage, reason: RecoveryReason) => observe(() => blocked(stage, reason)),
    fail,
    begin(trigger: RecoveryTrigger, binding: RecoveryBinding, inventory: RecoveryInventory) {
      return observe(() => {
        if (disposed || (episode && episode.closed === undefined)) return episode?.id
        if (!inventory.items.some(item => item.item_kind === 'view')) return
        if (exhausted || count >= MAX_RECOVERY_EPISODES || inventory.items.length > MAX_RECOVERY_ITEMS) {
          coverageLost('episode')
          return
        }
        const items = inventory.items.map(item => ({
          item_kind: item.item_kind,
          item_id: item.item_id,
          ...(item.item_kind === 'command' && item.revision_id ? {revision_id: item.revision_id} : {}),
          ...(item.item_kind !== 'view' && item.turn_id ? {turn_id: item.turn_id} : {}),
        }))
        if (
          items.some(item => !identifier(item.item_id) || (item.revision_id && !identifier(item.revision_id))) ||
          new Set(items.map(itemKey)).size !== items.length
        ) {
          void report('observer')
          coverageLost('inventory')
          return
        }
        const start = dependencies.now()
        const frozenBinding = {
          task_id: binding.task_id,
          session_id: binding.session_id,
          environment_id: binding.environment_id,
          ...(binding.chat_id ? {chat_id: binding.chat_id} : {}),
        }
        episode = {
          id: `${context.source_id}/${++count}`,
          eventId: '',
          start,
          trigger,
          surface: typeof context.surface === 'function' ? context.surface() : context.surface,
          binding: frozenBinding,
          inventory: {...inventory, items},
          items: new Map(),
          sources: new Map(),
          available: new Map(),
          succeeded: new Map(),
          controls: new Set(),
          blocked: new Set(),
          path: 'unknown',
        }
        const command_count = items.filter(item => item.item_kind === 'command').length
        const tool_count = items.filter(item => item.item_kind === 'client_tool').length
        emit(
          {
            observation: 'episode_started',
            commands_manifest: inventory.commands_manifest,
            tools_manifest: inventory.tools_manifest,
            views_manifest: inventory.views_manifest,
            command_count,
            tool_count,
            view_count: items.filter(item => item.item_kind === 'view').length,
            commands_applicability:
              inventory.commands_manifest === 'complete' && command_count === 0 ? 'known_empty' : 'required',
            tools_applicability:
              inventory.tools_manifest === 'complete' && tool_count === 0 ? 'known_empty' : 'required',
          },
          start,
        )
        for (const item of items) {
          const declaration = emit({observation: 'item_declared', ...item, item_declaration_event_id: ''}, start)
          if (declaration) episode.items.set(itemKey(item), {item, declaration})
        }
        source({source_kind: 'prestate'}, start)
        for (const stage of RECOVERY_STAGES) blocked(stage, 'prerequisite_unavailable', start)
        if (dependencies.visibility() !== 'visible') {
          for (const stage of RECOVERY_STAGES) blocked(stage, 'visibility_interrupted', start)
        }
        return episode.id
      })
    },
    attempt(generation: string) {
      observe(() => {
        const current = episode
        if (!current || current.closed !== undefined || current.generation === generation) return
        const previous_attempt_event_id = current.attempt
        current.generation = generation
        for (const stage of RECOVERY_STAGES) {
          if (current.available.has(stage) && !current.succeeded.has(stage)) current.incompleteAttempt = true
          current.available.delete(stage)
          current.succeeded.delete(stage)
        }
        current.controls.clear()
        current.blocked.clear()
        for (const item of current.items.values()) {
          if (item.disposition && !terminal(item.disposition)) item.disposition = undefined
        }
        emit(
          {observation: 'attempt_started', ...(previous_attempt_event_id ? {previous_attempt_event_id} : {})},
          dependencies.now(),
        )
      })
    },
    source,
    stateInput(reference: RecoverySourceReference) {
      observe(() => available('state_application', 'provided_state', reference))
    },
    initiated(reference: RecoverySourceReference) {
      observe(() => {
        if (reference.source_kind === 'reconnect_initiated') succeed('reconnect', reference)
      })
    },
    stateApplied(reference: RecoverySourceReference, sessionId: string, chatId: string, consistent: boolean) {
      observe(() => {
        const current = episode
        if (!current || !sourceValid(reference)) return
        const time = dependencies.now()
        if (!current.binding.chat_id || !reference.source_session_id || !reference.source_chat_id) {
          blocked('state_application', 'state_unverified', time)
          return
        }
        if (
          sessionId !== current.binding.session_id ||
          chatId !== current.binding.chat_id ||
          reference.source_session_id !== sessionId ||
          reference.source_chat_id !== chatId
        ) {
          fail('state_application', 'binding_mismatch', time)
          return
        }
        if (!consistent) return fail('state_application', 'state_mismatch', time)
        current.controls.clear()
        current.succeeded.delete('controls')
        succeed(
          'state_application',
          reference,
          {applied_session_id: sessionId, applied_chat_id: chatId, state_consistent: true},
          time,
        )
        const applied = source(
          {
            source_kind: 'action',
            source_session_id: sessionId,
            source_chat_id: chatId,
            state_path: reference.state_path,
          },
          time,
        )
        if (!applied) return
        reconcileStage('pending_commands', applied)
        reconcileStage('client_tools', applied)
        if (dependencies.role() === 'unknown') blocked('controls', 'role_unknown', time)
        else available('controls', 'role_and_view', applied)
      })
    },
    disposition(item: RecoveryItem, disposition: RecoveryDisposition, reference: RecoverySourceReference) {
      observe(() => {
        const current = episode
        const declared = current?.items.get(itemKey(item))
        if (!current || !declared || !sourceValid(reference) || current.closed !== undefined) return
        const stage = item.item_kind === 'command' ? 'pending_commands' : 'client_tools'
        const start = current.available.get(stage)
        if (!start || (item.item_kind === 'command' && !item.revision_id)) return
        if (disposition.disposition === 'system_failed') {
          const commandFailure = item.item_kind === 'command' && disposition.evidence_kind === 'host_action'
          const toolFailure =
            item.item_kind === 'client_tool' &&
            disposition.native_failure_code &&
            ['host_action', 'host_snapshot_terminal'].includes(disposition.evidence_kind)
          if (!commandFailure && !toolFailure) return
        }
        if (disposition.disposition === 'user_withdrawn' && disposition.evidence_kind !== 'local_withdrawal') return
        if (disposition.evidence_kind === 'unknown' && disposition.disposition !== 'unresolved') return
        const time = dependencies.now()
        const projected: RecoveryDisposition = {
          disposition: disposition.disposition,
          evidence_kind: disposition.evidence_kind,
          ...(disposition.native_failure_code ? {native_failure_code: disposition.native_failure_code} : {}),
        }
        emit(
          {
            observation: 'item_disposition',
            ...declared.item,
            ...projected,
            item_declaration_event_id: declared.declaration,
            stage_available_event_id: start.id,
            source_event_id: reference.event_id,
          },
          time,
        )
        declared.disposition = projected
        reconcileStage(stage, reference, time)
      })
    },
    controls(viewId: string, proof: RecoveryControls, reference: RecoverySourceReference) {
      observe(() => {
        const current = episode
        const declared = current?.items.get(itemKey({item_kind: 'view', item_id: viewId}))
        const start = current?.available.get('controls')
        const state = current?.succeeded.get('state_application')
        if (!current || !declared || !start || !state || !sourceValid(reference) || current.closed !== undefined) return
        if (proof.role === 'unknown') return blocked('controls', 'role_unknown', reference.time)
        const expectedRole = dependencies.role()
        const roleMatches = proof.role === expectedRole || (expectedRole === 'writable' && proof.role === 'read_only')
        if (
          proof.session_id !== current.binding.session_id ||
          proof.environment_id !== current.binding.environment_id ||
          proof.chat_id !== current.binding.chat_id ||
          !roleMatches
        ) {
          return fail('controls', 'binding_mismatch', reference.time)
        }
        const blockedRole = proof.role === 'passive' || proof.role === 'read_only'
        const valid = blockedRole
          ? !proof.input_editable &&
            !proof.send_enabled_for_valid_input &&
            !proof.stop_present &&
            proof.action_mode === 'blocked'
          : proof.input_editable &&
            proof.send_enabled_for_valid_input &&
            (proof.active_turn_id
              ? proof.stop_present && ['steer', 'queue'].includes(proof.action_mode)
              : !proof.stop_present && proof.action_mode === 'send')
        if (!valid) return fail('controls', 'controls_mismatch', reference.time)
        emit(
          {
            observation: 'controls_observed',
            client_view_id: viewId,
            item_declaration_event_id: declared.declaration,
            stage_available_event_id: start.id,
            state_application_event_id: state.id,
            source_event_id: reference.event_id,
            session_id: proof.session_id,
            environment_id: proof.environment_id,
            chat_id: proof.chat_id,
            role: proof.role,
            ...(proof.active_turn_id ? {active_turn_id: proof.active_turn_id} : {}),
            control_scenario: blockedRole ? proof.role : proof.active_turn_id ? 'active' : 'settled_writable',
            action_mode: proof.action_mode,
            input_editable: proof.input_editable,
            send_enabled_for_valid_input: proof.send_enabled_for_valid_input,
            stop_present: proof.stop_present,
          },
          reference.time,
        )
        current.controls.add(viewId)
        const views = [...current.items.values()].filter(value => value.item.item_kind === 'view')
        if (
          current.inventory.views_manifest === 'complete' &&
          views.every(value => current.controls.has(value.item.item_id))
        ) {
          succeed('controls', reference)
        }
      })
    },
    dispose() {
      observe(() => {
        if (disposed) return
        for (const stage of RECOVERY_STAGES) if (!episode?.succeeded.has(stage)) blocked(stage, 'disposed')
        disposed = true
        episode = undefined
      })
    },
  }
}

export type BrowserClientRecoveryTracker = ReturnType<typeof createBrowserClientRecoveryTracker>
